import { clampPosterUnit, getPosterImageFrameStyle } from './posterFocal';
import { EVENT_POSTER_CONSTANTS } from '@/lib/api/apiTypes';
import * as React from 'react';

export type FlyerImageSource = {
  public_url: string;
  position?: number;
  focal_point_x?: number;
  focal_point_y?: number;
  focal_scale?: number;
};

export function FlyerImage({
  className,
  image,
  maxNaturalHeight,
  onError,
  onLoadEnd,
  style,
}: {
  className?: string;
  image: FlyerImageSource;
  maxNaturalHeight?: number;
  onError?: () => void;
  onLoadEnd?: () => void;
  style?: React.CSSProperties;
}) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = React.useState<{
    url: string;
    width: number;
    height: number;
  } | null>(null);
  const activeNaturalSize = naturalSize?.url === image.public_url ? naturalSize : null;
  const focalPointX = clampPosterUnit(
    image.focal_point_x ?? EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x
  );
  const focalPointY = clampPosterUnit(
    image.focal_point_y ?? EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y
  );
  const focalScale = Math.max(1, image.focal_scale ?? 1);
  const naturalRenderHeight =
    maxNaturalHeight && activeNaturalSize && containerSize.width
      ? Math.min(
          maxNaturalHeight,
          (containerSize.width * activeNaturalSize.height) / activeNaturalSize.width
        )
      : containerSize.height;
  const frameStyle = getPosterImageFrameStyle(
    activeNaturalSize?.width ?? null,
    activeNaturalSize?.height ?? null,
    containerSize.width,
    naturalRenderHeight,
    focalPointX,
    focalPointY,
    focalScale
  );
  const isFrameReady = Boolean(frameStyle);

  React.useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      globalThis.addEventListener('resize', updateSize);
      return () => globalThis.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height: maxNaturalHeight ? naturalRenderHeight || maxNaturalHeight : '100%',
        maxHeight: maxNaturalHeight,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        ...style,
      }}>
      <img
        alt=""
        draggable={false}
        onError={onError}
        onLoad={(event) => {
          setNaturalSize({
            url: image.public_url,
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          });
          onLoadEnd?.();
        }}
        src={image.public_url}
        style={{
          height: frameStyle?.height ?? '100%',
          left: frameStyle?.left ?? 0,
          maxWidth: 'none',
          objectFit: frameStyle ? 'fill' : 'cover',
          objectPosition: `${focalPointX * 100}% ${focalPointY * 100}%`,
          opacity: isFrameReady ? 1 : 0,
          position: 'absolute',
          top: frameStyle?.top ?? 0,
          transition: 'opacity 120ms ease-out',
          width: frameStyle?.width ?? '100%',
          display: 'block',
        }}
      />
    </div>
  );
}
